import config from '../../config/config';

import deleteRequest from './base/delete';
import getRequest from './base/get';
import patchRequest from './base/patch';
import postRequest from './base/post';

interface QueryParameters {
  limit?: number;
  [key: string]: any;
}

export default {
  /**
   * Fetch the charging point list with pagination
   * @param {any} query - The query
   */
  fetchChargingPoints: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query, limit: query?.limit || 5 };
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-point`, {
      ...parameters,
    });
  },
  /**
   * Fetch the charging point list with pagination
   */
  fetchChargingPointFilterList: (): Promise<any> => {
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-point/list-filters`);
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} data - The data
   */
  fetchChargingPointWithoutPagination: (data: any): Promise<any> => {
    return getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-point`, { ...data });
  },
  /**
   * Fetch the charging station list with pagination
   * @param {any} id - The id
   */
  fetchChargingPointGeneralDetails: (id: string): Promise<any> => {
    return getRequest(`${config.cpo.commonApiUrl}api/common/v1/charging-point/${id}`);
  },
  /**
   * Fetch the charging station list with pagination
   * @param {any} id - The id
   */
  fetchChargingPointLocation: (id: string): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/charging-point-location/${id}`,
    );
  },
  /**
   * Fetch the charging station list with pagination
   * @param {any} data - The data
   */
  fetchRfidCards: (data: any): Promise<any> => {
    return getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-point/rfid-cards`, {
      ...data,
    });
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} data - The data
   */
  fetchChargingSchedules: (data: any): Promise<any> => {
    return getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-point/charging-schedule`, {
      ...data,
    });
  },
  /**
   * Fetch the charging point list with pagination
   */
  fetchChargingScheduleFilters: (): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/list-charging-schedule-filter`,
    );
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} data - The data
   */
  fetchMaintenanceSchedules: (data: any): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/maintenance-schedule`,
      {
        ...data,
      },
    );
  },
  /**
   * Fetch the charging point list with pagination
   */
  fetchMaintenanceScheduleFilters: (): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/list-maintenance-schedule-filter`,
    );
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} data - The data
   */
  fetchMaintenanceScheduleReport: (data: any): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/maintenance-schedule-report`,
      { ...data },
    );
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} data - The data
   */
  fetchUpcomingMaintenanceSchedule: (data: any): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/upcoming-maintenance-schedule`,
      { ...data },
    );
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} data - The data
   */
  fetchGuestCharging: (data: any): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/free-charging-schedule`,
      { ...data },
    );
  },
  /**
   * Fetch the charging point list with pagination
   */
  fetchListGuestChargingFilters: (): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/list-free-charging-filter`,
    );
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} data - The data
   */
  fetchChargingScheduleReport: (data: any): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/charging-schedule-report`,
      { ...data },
    );
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} data - The data
   */
  fetchDeviceTransaction: (data: any): Promise<any> => {
    const sn = data?.chargingPointSn || data?.chargingpointsn;
    const accountCustomIdValue = data?.accountCustomId;
    const station = data?.stationId || data?.chargingStation;

    const parameters: Record<string, any> = { ...data };
    delete parameters.chargingpointsn;
    delete parameters.chargingPointSn;
    delete parameters.accountCustomId;
    delete parameters.chargingStation;
    delete parameters.stationId;

    if (sn) {
      parameters.chargingPointSn = Array.isArray(sn) ? sn : [sn];
    }
    if (accountCustomIdValue) {
      parameters.accountCustomId = Array.isArray(accountCustomIdValue)
        ? accountCustomIdValue
        : [accountCustomIdValue];
    }
    if (station) {
      parameters.stationId = Array.isArray(station) ? station : [station];
    }

    return getRequest(
      `${config.cpo.transactionApiUrl}api/common/v1/charging-transaction/transaction-list`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch the charging point list with pagination
   */
  fetchListTransactionFilters: (): Promise<any> => {
    return getRequest(
      `${config.cpo.transactionApiUrl}api/common/v1/charging-transaction/transaction-list/filter-list`,
    );
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} data - The data
   */
  fetchTransactionReport: (data: any): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/power-consumption-report`,
      { ...data },
    );
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} data - The data
   */
  fetchConnectorHistory: (data: any): Promise<any> => {
    return getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-point/connector-history`, {
      ...data,
    });
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} data - The data
   */
  fetchDeviceAlerts: (data: any): Promise<any> => {
    return getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-point/alerts`, {
      ...data,
    });
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} data - The data
   */
  fetchAlertReport: (data: any): Promise<any> => {
    return getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-point/alerts-report`, {
      ...data,
    });
  },
  /**
   * Fetch the charging station list with pagination
   * @param {any} id - The id
   * @param {any} data - The data
   */
  updateFreeChargingSchedule: (id: string, data: any): Promise<any> => {
    return patchRequest(`${config.reportApiUrl}api/super-admin/v1/free-charging-schedule/${id}`, {
      ...data,
    });
  },
  /**
   * Delete the charging point
   * @param {any} id - The id
   */
  deleteChargingPoint: (id: string): Promise<any> => {
    return deleteRequest(`${config.commonApiUrl}api/super-admin/v1/charging-point/${id}`);
  },
  /**
   * Get charging point details by Id
   * @param {any} id - The id
   */
  getChargingPointById: (id: string): Promise<any> => {
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-point/${id}`);
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} data - The data
   */
  fetchOcppLogs: (data: any): Promise<any> => {
    return getRequest(`${config.reportApiUrl}api/super-admin/v1/ocpp-logs`, {
      ...data,
    });
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} id - The id
   * @param {any} queryParameters - The queryParameters
   */
  fetchChargingPointConceptionMonthWise: (id: string, queryParameters: any): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/charging-point-consumption-month-wise/${id}`,
      {
        ...queryParameters,
      },
    );
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} id - The id
   * @param {any} queryParameters - The queryParameters
   */
  fetchChargingPointConceptionByDate: (id: string, queryParameters: any): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/charging-point-consumption-by-date/${id}`,
      {
        ...queryParameters,
      },
    );
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} id - The id
   * @param {any} queryParameters - The queryParameters
   */
  fetchConnectorStatusByDate: (id: string, queryParameters: any): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/connector-status-by-date/${id}`,
      {
        ...queryParameters,
      },
    );
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} id - The id
   * @param {any} queryParameters - The queryParameters
   */
  fetchDeviceStatusByDate: (id: string, queryParameters: any): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/device-status-by-date/${id}`,
      {
        ...queryParameters,
      },
    );
  },
  /**
   * Fetch the charging point list with pagination
   * @param {any} id - The id
   * @param {any} queryParameters - The queryParameters
   */
  fetchDeviceStatusMonthWise: (id: string, queryParameters: any): Promise<any> => {
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/device-status-month-wise/${id}`,
      {
        ...queryParameters,
      },
    );
  },
  /**
   * fetch the charging point status
   * @param {any} id - The charging point ID
   * @param {any} data - The availability status data
   * @returns {any} A promise that resolves with the update response
   */
  editDeviceAvailableStatus: (id: string, data: any): Promise<any> => {
    return patchRequest(
      `${config.commonApiUrl}api/super-admin/v1/charging-point/status/${id}`,
      data,
      false,
    );
  },
  /**
   * edit the charging point status
   * @param {any} id - The charging point ID
   * @param {any} data - The status data to edit
   * @returns {any} A promise that resolves with the update response
   */
  editDeviceStatus: (id: string, data: any): Promise<any> => {
    return patchRequest(
      `${config.commonApiUrl}api/super-admin/v1/free-charging/updateActiveStatus/${id}`,
      data,
      false,
    );
  },
  /**
   * get  Blocked Websocket Devices
   * @param {any} query - The query
   */
  fetchBlockedWebsocket: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/ocpp-ws-monitoring/blocked-cps`, {
      ...parameters,
    });
  },

  /**
   * get  Never Blocked Websocket Devices
   * @param {any} query - The query
   */
  fetchNeverBlockedWebsocket: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/ocpp-ws-monitoring/never-block-cps`,
      {
        ...parameters,
      },
    );
  },

  /**
   * get error logs websockets devices
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the error logs
   */
  fetchErrorLogs: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/ocpp-ws-monitoring/error-logs`, {
      ...parameters,
    });
  },

  /**
   * add never block cp
   * @param {any} id - The id
   */
  addNeverBlockCP: (id: string): Promise<any> => {
    return postRequest(
      `${config.commonApiUrl}api/super-admin/v1/ocpp-ws-monitoring/add-never-block-cp`,
      { sn: id },
    );
  },
  /**
   * Edit Un Blocked Websocket
   * @param {any} id - The id
   * @param {any} query - The query
   */
  editUnBlockedWebsocket: (id: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return patchRequest(
      `${config.commonApiUrl}api/super-admin/v1/ocpp-ws-monitoring/unblock-cp/${id}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Delete the Never Block CP
   * @param {any} id - The id
   */
  deleteNeverBlockCp: (id: string): Promise<any> => {
    return deleteRequest(
      `${config.commonApiUrl}api/super-admin/v1/ocpp-ws-monitoring/remove-never-block-cp/${id}`,
    );
  },
  /**
   * Fetching device image
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the device image data
   */
  fetchDeviceImage: (query: QueryParameters = {}) => {
    const parameters = { ...query };
    return getRequest(
      `${config.commonApiUrl}api/facility/v1/charging-point-models/charge-point-metadata`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Add a new device
   * @param {any} data - The data
   */
  addDevice: (data: any): Promise<any> => {
    return postRequest(`${config.commonApiUrl}api/super-admin/v1/charging-point`, data);
  },
  /**
   * Edit charging point details
   * @param {any} id - The id
   * @param {any} data - The data
   * @param {any} isFileUpload - The isFileUpload
   */
  editChargingPoint: (id: string, data: any, isFileUpload = false): Promise<any> => {
    return patchRequest(`${config.commonApiUrl}api/common/v1/charging-point/${id}`, data, isFileUpload);
  },
};
